import { Box, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import React from "react";

import { firstEventKinds, type NewMember } from "../helpers/newMembers";

interface Props {
  members: NewMember[];
  /** Whether she has been messaged, from the shared outreach guard. */
  messaged?: (member: NewMember) => boolean;
}

/** One labelled bar. `total` of 0 renders empty rather than dividing by zero. */
const Bar: React.FC<{
  label: string;
  value: number;
  total: number;
  colour?: string;
  indent?: boolean;
}> = ({ label, value, total, colour, indent }) => (
  <Box sx={{ mb: 0.75, pl: indent ? 2 : 0 }}>
    <Box display="flex" justifyContent="space-between" gap={1}>
      <Typography
        variant="caption"
        sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        {value} · {total > 0 ? Math.round((value / total) * 100) : 0}%
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={total > 0 ? (value / total) * 100 : 0}
      sx={{
        height: 6,
        borderRadius: 3,
        ...(colour ? { "& .MuiLinearProgress-bar": { backgroundColor: colour } } : {}),
      }}
    />
  </Box>
);

const Section: React.FC<{ title: string; note?: string; children: React.ReactNode }> = ({
  title,
  note,
  children,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {title}
    </Typography>
    {note && (
      <Typography variant="caption" color="text.secondary" display="block">
        {note}
      </Typography>
    )}
    <Stack sx={{ mt: 0.5 }}>{children}</Stack>
  </Box>
);

/** Members counted by one of their answers, most common first. */
const byAnswer = (
  members: NewMember[],
  label: (member: NewMember) => string | null | undefined,
) => {
  const counts = new Map<string, NewMember[]>();
  for (const member of members) {
    const key = label(member) || "No answer";
    counts.set(key, [...(counts.get(key) ?? []), member]);
  }
  return [...counts.entries()].sort((a, b) => b[1].length - a[1].length);
};

const LOW = "#ef9a9a";
const MUTED = "#bdbdbd";

/**
 * What this batch of new members looks like as a group.
 *
 * The table answers "who do I write to next"; this answers "is the signup flow
 * working" — what they told us, what they did about a first event, and whether
 * they've come to anything since. It reads only the rows currently filtered, so
 * narrowing the table narrows the picture with it.
 */
const NewMemberInsights: React.FC<Props> = ({ members, messaged }) => {
  const total = members.length;
  if (total === 0) {
    return null;
  }

  const came = (member: NewMember) => member.eventsAttended > 0;
  const kind = (value: ReturnType<typeof firstEventKinds>[number]) =>
    members.filter((member) => firstEventKinds(member).includes(value)).length;
  const booked = kind("booked");
  const guests = members.filter(
    (member) => member.status === "guest" && member.guestTokensLeft != null,
  );
  const holding = members.filter((member) => member.suggestion.invitation).length;
  const suggested = (programme: string) =>
    members.filter(
      (member) => !member.suggestion.invitation && member.suggestion.event?.programme === programme,
    ).length;
  const typeLabels = new Map(
    members.flatMap((member) =>
      member.suggestion.byType.map((type) => [type.programme, type.label] as const),
    ),
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        📊 Insights — the {total} member{total === 1 ? "" : "s"} shown
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        With this few members one person moves a bar a long way, so read these as a picture rather
        than a result.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
        }}
      >
        <Section title="Her first event, picked at signup">
          <Bar label="Booked one" value={booked} total={total} />
          <Bar label="came to it" value={kind("came")} total={booked} indent />
          <Bar label="missed it" value={kind("missed")} total={booked} colour={LOW} indent />
          <Bar label="still to come" value={kind("ahead")} total={booked} colour={MUTED} indent />
          <Bar label="Skipped booking one" value={kind("skipped")} total={total} colour={LOW} />
        </Section>

        <Section title="Since she signed up">
          <Bar
            label="Came to at least one event"
            value={members.filter(came).length}
            total={total}
          />
          <Bar
            label="Said yes to an event still ahead"
            value={members.filter((member) => member.acceptedUpcoming > 0).length}
            total={total}
          />
          <Bar label="Holds an invitation she can open" value={holding} total={total} />
          <Bar
            label="Nothing booked or attended"
            value={
              members.filter((member) => !came(member) && member.acceptedUpcoming === 0).length
            }
            total={total}
            colour={LOW}
          />
        </Section>

        <Section
          title="What her message would suggest"
          note="Her Invitations first, otherwise the rules' pick."
        >
          <Bar label="Her Invitations (she holds one)" value={holding} total={total} />
          {[...typeLabels.entries()].map(([programme, label]) => (
            <Bar key={programme} label={label} value={suggested(programme)} total={total} />
          ))}
          <Bar
            label="No event she can open"
            value={
              members.filter((member) => !member.suggestion.invitation && !member.suggestion.event)
                .length
            }
            total={total}
            colour={MUTED}
          />
        </Section>

        <Section title="Why she wants spoken English">
          {byAnswer(members, (member) => member.priorityLabel).map(([label, group]) => (
            <Bar key={label} label={label} value={group.length} total={total} />
          ))}
        </Section>

        <Section title="What makes practising hard">
          {byAnswer(members, (member) => member.challengeLabel).map(([label, group]) => (
            <Bar key={label} label={label} value={group.length} total={total} colour={LOW} />
          ))}
        </Section>

        <Section
          title="Did her answer match what she did?"
          note="Of those who gave each answer about this week, how many have come to an event."
        >
          {byAnswer(members, (member) => member.readinessLabel).map(([label, group]) => (
            <Bar
              key={label}
              label={`${label} (${group.length})`}
              value={group.filter(came).length}
              total={group.length}
            />
          ))}
        </Section>

        {guests.length > 0 && (
          <Section title="Guest Pass events used" note={`Of the ${guests.length} Guests shown.`}>
            {[0, 1, 2, 3].map((used) => (
              <Bar
                key={used}
                label={`${used} of 3 used`}
                value={guests.filter((member) => 3 - (member.guestTokensLeft ?? 0) === used).length}
                total={guests.length}
              />
            ))}
          </Section>
        )}

        {messaged && (
          <Section
            title="Messaged, and came"
            note="The share who have come to an event, by whether we've messaged her. Not proof the message did it."
          >
            {[true, false].map((wasMessaged) => {
              const group = members.filter((member) => messaged(member) === wasMessaged);
              return (
                <Bar
                  key={String(wasMessaged)}
                  label={`${wasMessaged ? "Messaged" : "Not messaged yet"} (${group.length})`}
                  value={group.filter(came).length}
                  total={group.length}
                />
              );
            })}
          </Section>
        )}
      </Box>
    </Paper>
  );
};

export default NewMemberInsights;
